"use client";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDatabase } from "@/context/DatabaseContext";
import { setCookie } from "@/utils/cookie-utils";
import { resetDemoDataAction } from "@/utils/serverActions/demoActions";
import { signOut } from "@/utils/serverActions/authActions";
import { Database, Server } from "lucide-react";
import React from "react";
import { DatabaseEnvironment } from "@/context/DatabaseContext";

const switchToEnvironment = async (newValue: DatabaseEnvironment) => {
  setCookie("db_env", newValue, { path: "/" });
  await signOut();

  if (typeof window !== "undefined") {
    window.location.href = "/signin";
  }
};

export const DatabaseToggle: React.FC = () => {
  const { currentDatabase, setCurrentDatabase, isDevelopmentMode } =
    useDatabase();
  const [showResetDialog, setShowResetDialog] = React.useState(false);
  const [isResetting, setIsResetting] = React.useState(false);
  const [resetError, setResetError] = React.useState<string | null>(null);

  // Only show in development
  if (!isDevelopmentMode) {
    return null;
  }

  const handleValueChange = async (newValue: string) => {
    if (
      newValue !== "local" &&
      newValue !== "production" &&
      newValue !== "demo"
    ) {
      return;
    }

    if (newValue === "demo" && currentDatabase !== "demo") {
      setResetError(null);
      setShowResetDialog(true);
      return;
    }

    setCurrentDatabase(newValue);
    await switchToEnvironment(newValue);
  };

  const handleConfirmReset = async () => {
    setIsResetting(true);
    setResetError(null);

    try {
      await resetDemoDataAction();
      setShowResetDialog(false);
      setCurrentDatabase("demo");
      await switchToEnvironment("demo");
    } catch (error) {
      setResetError(
        error instanceof Error
          ? error.message
          : "No se pudieron reiniciar los datos de demo.",
      );
    } finally {
      setIsResetting(false);
    }
  };

  const handleSkipReset = async () => {
    setShowResetDialog(false);
    setCurrentDatabase("demo");
    await switchToEnvironment("demo");
  };

  const getDisplayInfo = (db: DatabaseEnvironment) => {
    switch (db) {
      case "local":
        return { icon: <Database className="h-4 w-4" />, label: "Local" };
      case "production":
        return { icon: <Server className="h-4 w-4" />, label: "Prod" };
      case "demo":
        return { icon: <Server className="h-4 w-4" />, label: "Demo" };
      // No default needed if DatabaseEnvironment is exhaustive
    }
  };

  const { icon: currentIcon, label: currentLabel } =
    getDisplayInfo(currentDatabase);

  return (
    <>
      <Select onValueChange={handleValueChange} value={currentDatabase}>
        <SelectTrigger
          size="xs"
          className={`w-[100px] flex items-center gap-2 transition-colors ${
            currentDatabase === "demo"
              ? "bg-black text-white border-gray-400 hover:bg-gray-700"
              : currentDatabase === "production"
                ? "bg-white text-red-600 border-red-600 hover:bg-white"
                : "bg-white text-green-600 border-green-600 hover:bg-white"
          }`}
        >
          <SelectValue placeholder={currentLabel}>
            <span className="flex items-center gap-2">
              {currentIcon}
              <span className="hidden sm:inline">{currentLabel}</span>
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="local">
            <span className="flex items-center gap-2">
              <Database className="h-4 w-4" /> Local
            </span>
          </SelectItem>
          <SelectItem value="production">
            <span className="flex items-center gap-2">
              <Server className="h-4 w-4" /> Prod
            </span>
          </SelectItem>
          <SelectItem value="demo">
            <span className="flex items-center gap-2">
              <Server className="h-4 w-4" /> Demo
            </span>
          </SelectItem>
        </SelectContent>
      </Select>

      <AlertDialog
        open={showResetDialog}
        onOpenChange={(open) => {
          if (!open && !isResetting) {
            setShowResetDialog(false);
          }
        }}
      >
        <AlertDialogContent variant="warning">
          <AlertDialogHeader variant="warning">
            <AlertDialogTitle variant="warning">
              Reiniciar datos de demo
            </AlertDialogTitle>
            <AlertDialogDescription variant="warning">
              ¿Quieres restaurar el demo antes de entrar? Se recargan los datos
              anonimizados y la conciliación bancaria queda lista con su
              extracto (lo mismo que «Restaurar demo» en Ajustes → Demo). Toma
              alrededor de un minuto y se pierde cualquier cambio que hayas
              hecho en el demo anterior.
              {resetError && (
                <span className="block mt-2 text-red-600">{resetError}</span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isResetting} onClick={handleSkipReset}>
              No, mantener como está
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={isResetting}
              onClick={(event) => {
                event.preventDefault();
                handleConfirmReset();
              }}
            >
              {isResetting ? "Reiniciando..." : "Sí, reiniciar datos"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
